import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Grid,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Replay as ReplayIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterIcon,
  GetApp as ExportIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import whatsappService from '../services/whatsappService';
import {
  WhatsAppMessage,
  MessageFilters
} from '../types/whatsapp';
import { usePermissions } from '../hooks/usePermissions';

const WhatsAppHistoryPage: React.FC = () => {
  const { hasPermission } = usePermissions();
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<WhatsAppMessage | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  
  // التحقق من الصلاحيات
  const canViewDetails = hasPermission('marketing', 'whatsapp-history', 'view');
  const canResend = hasPermission('marketing', 'whatsapp-history', 'resend');
  const canDelete = hasPermission('marketing', 'whatsapp-history', 'delete');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [filters, setFilters] = useState<MessageFilters>({
    status: '',
    phoneNumber: '',
    messageType: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    loadMessages();
  }, [page, rowsPerPage]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const response = await whatsappService.getMessageHistory(
        filters,
        page + 1,
        rowsPerPage
      );
      setMessages(response.messages);
      setTotalCount(response.pagination.total);
    } catch (error: any) {
      showAlert('error', error.message || 'فشل تحميل السجل');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type: 'success' | 'error', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleApplyFilters = () => {
    setPage(0);
    loadMessages();
    setFilterOpen(false);
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      phoneNumber: '',
      messageType: '',
      startDate: '',
      endDate: ''
    });
    setPage(0);
  };

  const handleViewDetails = (message: WhatsAppMessage) => {
    setSelectedMessage(message);
    setDetailsOpen(true);
  };

  const handleResend = async (message: WhatsAppMessage) => {
    if (!window.confirm('هل تريد إعادة إرسال هذه الرسالة؟')) return;

    try {
      await whatsappService.resendMessage(message._id);
      showAlert('success', 'تم إعادة إرسال الرسالة بنجاح');
      loadMessages();
    } catch (error: any) {
      showAlert('error', error.message || 'فشل إعادة الإرسال');
    }
  };

  const handleDelete = async (message: WhatsAppMessage) => {
    if (!window.confirm('هل تريد حذف هذه الرسالة من السجل؟')) return;

    try {
      await whatsappService.deleteMessage(message._id);
      showAlert('success', 'تم حذف الرسالة بنجاح');
      loadMessages();
    } catch (error: any) {
      showAlert('error', error.message || 'فشل حذف الرسالة');
    }
  };

  const formatDate = (date: string) => {
    if (!date) return '-';
    return format(new Date(date), 'dd/MM/yyyy HH:mm', { locale: ar });
  };

  const getStatusChip = (status: string) => {
    const color = whatsappService.getStatusColor(status);
    const text = whatsappService.getStatusText(status);
    
    return (
      <Chip
        label={text}
        size="small"
        sx={{
          bgcolor: `${color}.100`,
          color: `${color}.800`,
          fontWeight: 600
        }}
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          سجل رسائل واتساب
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setFilterOpen(true)}
          >
            فلترة
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadMessages}
          >
            تحديث
          </Button>
        </Box>
      </Box>

      {/* Alert */}
      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {/* Active Filters */}
      {(filters.status || filters.phoneNumber || filters.messageType || filters.startDate) && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            الفلاتر النشطة:
          </Typography>
          {filters.status && (
            <Chip
              label={`الحالة: ${whatsappService.getStatusText(filters.status as string)}`}
              size="small"
              onDelete={() => setFilters({ ...filters, status: '' })}
            />
          )}
          {filters.phoneNumber && (
            <Chip
              label={`الرقم: ${filters.phoneNumber}`}
              size="small"
              onDelete={() => setFilters({ ...filters, phoneNumber: '' })}
            />
          )}
          {filters.messageType && (
            <Chip
              label={`النوع: ${whatsappService.getMessageTypeText(filters.messageType)}`}
              size="small"
              onDelete={() => setFilters({ ...filters, messageType: '' })}
            />
          )}
          <Button size="small" onClick={handleClearFilters}>
            مسح الكل
          </Button>
        </Box>
      )}

      {/* Messages Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>التاريخ</TableCell>
                <TableCell>المستلم</TableCell>
                <TableCell>نوع الرسالة</TableCell>
                <TableCell>المحتوى</TableCell>
                <TableCell>الحالة</TableCell>
                <TableCell>المزود</TableCell>
                <TableCell align="center">الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : messages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">
                      لا توجد رسائل
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                messages.map((message) => (
                  <TableRow key={message._id} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(message.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {message.recipient.contactName || message.recipient.phoneNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {message.recipient.fullPhoneNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={whatsappService.getMessageTypeText(message.messageType)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {message.templateName || message.content}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(message.status)}
                      {message.status === 'delivered' && message.deliveredAt && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {formatDate(message.deliveredAt)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {message.providerSlug}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {canViewDetails && (
                        <Tooltip title="عرض التفاصيل">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(message)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canResend && message.status === 'failed' && message.retryCount < message.maxRetries && (
                        <Tooltip title="إعادة الإرسال">
                          <IconButton
                            size="small"
                            onClick={() => handleResend(message)}
                          >
                            <ReplayIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip title="حذف">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(message)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="عدد الصفوف:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
        />
      </Card>

      {/* Filter Dialog */}
      <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>فلترة الرسائل</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>الحالة</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <MenuItem value="">الكل</MenuItem>
                  <MenuItem value="pending">قيد الانتظار</MenuItem>
                  <MenuItem value="sent">تم الإرسال</MenuItem>
                  <MenuItem value="delivered">تم التسليم</MenuItem>
                  <MenuItem value="read">تمت القراءة</MenuItem>
                  <MenuItem value="failed">فشل</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>نوع الرسالة</InputLabel>
                <Select
                  value={filters.messageType}
                  onChange={(e) => setFilters({ ...filters, messageType: e.target.value })}
                >
                  <MenuItem value="">الكل</MenuItem>
                  <MenuItem value="template">قالب</MenuItem>
                  <MenuItem value="text">نص</MenuItem>
                  <MenuItem value="media">وسائط</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="رقم الهاتف"
                value={filters.phoneNumber}
                onChange={(e) => setFilters({ ...filters, phoneNumber: e.target.value })}
                placeholder="ابحث برقم الهاتف"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="من تاريخ"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="إلى تاريخ"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterOpen(false)}>إلغاء</Button>
          <Button onClick={handleClearFilters}>مسح</Button>
          <Button onClick={handleApplyFilters} variant="contained">
            تطبيق
          </Button>
        </DialogActions>
      </Dialog>

      {/* Message Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>تفاصيل الرسالة</DialogTitle>
        <DialogContent>
          {selectedMessage && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  المستلم
                </Typography>
                <Typography variant="body1">
                  {selectedMessage.recipient.contactName || 'غير محدد'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedMessage.recipient.fullPhoneNumber}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  الحالة
                </Typography>
                {getStatusChip(selectedMessage.status)}
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  نوع الرسالة
                </Typography>
                <Typography variant="body1">
                  {whatsappService.getMessageTypeText(selectedMessage.messageType)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  المزود
                </Typography>
                <Typography variant="body1">
                  {selectedMessage.providerSlug}
                </Typography>
              </Grid>

              {selectedMessage.templateName && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    القالب
                  </Typography>
                  <Typography variant="body1">
                    {selectedMessage.templateName}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  المحتوى
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.50', mt: 1 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedMessage.content}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  تاريخ الإنشاء
                </Typography>
                <Typography variant="body2">
                  {formatDate(selectedMessage.createdAt)}
                </Typography>
              </Grid>

              {selectedMessage.sentAt && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    تاريخ الإرسال
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedMessage.sentAt)}
                  </Typography>
                </Grid>
              )}

              {selectedMessage.deliveredAt && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    تاريخ التسليم
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedMessage.deliveredAt)}
                  </Typography>
                </Grid>
              )}

              {selectedMessage.readAt && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    تاريخ القراءة
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedMessage.readAt)}
                  </Typography>
                </Grid>
              )}

              {selectedMessage.errorMessage && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <Typography variant="subtitle2">
                      رسالة الخطأ:
                    </Typography>
                    <Typography variant="body2">
                      {selectedMessage.errorMessage}
                    </Typography>
                  </Alert>
                </Grid>
              )}

              {selectedMessage.retryCount > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    عدد محاولات الإعادة
                  </Typography>
                  <Typography variant="body2">
                    {selectedMessage.retryCount} من {selectedMessage.maxRetries}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>إغلاق</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsAppHistoryPage;